'use client';

import { create } from 'zustand';
import type { Bookmark, AddBookmarkData } from '@/lib/db-bookmarks';
import { getBookmarks, addBookmark, removeBookmark } from '@/lib/db-bookmarks';

interface BookmarkState {
  bookmarks: Map<string, Bookmark>;
  loading: boolean;
  initialized: boolean;

  fetchBookmarks: (userId: string) => Promise<void>;
  toggleBookmark: (userId: string, eventData: Omit<AddBookmarkData, 'user_id'>) => Promise<void>;
  isBookmarked: (eventSlug: string) => boolean;
  clear: () => void;
}

export const useBookmarkStore = create<BookmarkState>((set, get) => ({
  bookmarks: new Map(),
  loading: false,
  initialized: false,

  fetchBookmarks: async (userId: string) => {
    set({ loading: true });
    try {
      const rows = await getBookmarks(userId);
      const map = new Map<string, Bookmark>();
      for (const b of rows) map.set(b.event_slug, b);
      set({ bookmarks: map, initialized: true });
    } catch {
      // Silently fail — bookmarks are non-critical
    } finally {
      set({ loading: false });
    }
  },

  toggleBookmark: async (userId: string, eventData: Omit<AddBookmarkData, 'user_id'>) => {
    const { bookmarks } = get();
    const slug = eventData.event_slug;
    const existing = bookmarks.get(slug);

    if (existing) {
      // Optimistic remove
      const next = new Map(bookmarks);
      next.delete(slug);
      set({ bookmarks: next });
      try {
        await removeBookmark(userId, slug);
        window._paq?.push(['trackEvent', 'Bookmark', 'Remove', slug]);
      } catch {
        // Rollback
        const rollback = new Map(get().bookmarks);
        rollback.set(slug, existing);
        set({ bookmarks: rollback });
      }
    } else {
      // Optimistic add
      const optimistic: Bookmark = {
        id: 'temp-' + Date.now(),
        user_id: userId,
        event_slug: slug,
        event_title: eventData.event_title,
        event_date_start: eventData.event_date_start ?? null,
        event_country: eventData.event_country ?? null,
        created_at: new Date().toISOString(),
      };
      const next = new Map(bookmarks);
      next.set(slug, optimistic);
      set({ bookmarks: next });
      try {
        const real = await addBookmark({ user_id: userId, ...eventData });
        const updated = new Map(get().bookmarks);
        updated.set(slug, real);
        set({ bookmarks: updated });
        window._paq?.push(['trackEvent', 'Bookmark', 'Add', slug]);
      } catch {
        // Rollback
        const rollback = new Map(get().bookmarks);
        rollback.delete(slug);
        set({ bookmarks: rollback });
      }
    }
  },

  isBookmarked: (eventSlug: string) => {
    return get().bookmarks.has(eventSlug);
  },

  clear: () => {
    set({ bookmarks: new Map(), initialized: false });
  },
}));
